import { useAuth, useGuest } from '../state';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ILocale } from '@/lang/types';
import { setLocalCookie } from '@/lang/utils';
import shallow from 'zustand/shallow';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

/**
 * Syncs the cookie, the guest and the user to the locale in the URL.
 *
 * The URL is the only source of truth. This hook used to switch a visitor with no
 * NEXT_LOCALE cookie to their `navigator.language` with a client-side
 * router.replace. Googlebot never keeps cookies and renders as en-US, so every
 * `/ar/…` page it rendered replaced itself with `/en/…`, and Search Console filed
 * the Arabic pages as "Page with redirect" instead of indexing them.
 *
 * First-visit language detection now happens only on the server, for a URL with
 * no locale prefix — see src/middleware.ts.
 */
export const useSyncLocalization = () => {
  const { locale: routerLanguage } = useRouter();
  const [guest, setGuest] = useGuest(s => [s.guest, s.setGuest], shallow);
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const [isUpdating, setIsUpdating] = useState(false);

  const queryClient = useQueryClient();

  // Seeded with the locale of the first render, so the effect below treats mount
  // as "no change". Without it, the unkeyed invalidateQueries() ran on every page
  // load and threw away the queries getServerSideProps had just prefetched and
  // dehydrated into the HTML — every one of them refetched immediately after
  // hydration, doubling the API calls for a page that had already rendered.
  const prevLocale = useRef(routerLanguage);

  const updateUserLang = useCallback(
    (language: string) => {
      setIsUpdating(true);
      setUser(prevState => ({
        ...prevState,
        language,
      }));
      setIsUpdating(false);
    },
    [setUser]
  );

  useEffect(() => {
    const language = routerLanguage;

    setLocalCookie(language as ILocale);

    if (user) {
      if (user.language?.toLowerCase() !== language?.toLowerCase() && !isUpdating) {
        updateUserLang(language as string);
      }
    }

    if (guest?.language?.toLowerCase() !== language?.toLowerCase()) {
      setGuest(prevState => {
        const newGuest = prevState ? { ...prevState } : {};
        return {
          ...newGuest,
          language,
        };
      });
    }

    if (prevLocale.current !== routerLanguage) {
      // Locale actually changed — cached responses are in the wrong language.
      // Reached by navigating straight to the other locale (an `/ar/…` link),
      // which does not go through switchLang; switchLang invalidates itself.
      prevLocale.current = routerLanguage;
      queryClient.invalidateQueries();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLanguage, setUser, setGuest, queryClient]);
};
